import { cloneDeep } from 'lodash'

import FirestoreService from '../services/firestoreService'

import { calculateAge } from '../models/interface.helper'
import { httpService } from '../services/httpService'
import { getAllClauses } from './clauses'

import { IDocument } from '../models/event-registered-users/event-registered-users.interface'
import { IRiderTeamMemberInterface } from '../models/rider-team-member/riderTeamMember.interface'
import { UserModel } from '../models/users/user.model'
import { IEventDetails } from '../models/event-details/event-details.interface'
import { IUserPublicInterface } from '../models/users-public/users-public.interface'
import { IGenerateDocumentPayload } from '../types/document'

import { signedStatuses } from '../const/data/v01/rider-team-members/firestore-v01-rider-team-member-const'

import { CONST } from '../const/const'

interface EligibleToSignProps {
  paperworkDocuments: IDocument[]
  ridersTeamMembers: IRiderTeamMemberInterface[]
  userId: string
}

export const eligibleToSign = ({
  paperworkDocuments,
  ridersTeamMembers,
  userId,
}: EligibleToSignProps) => {
  let notSignedOwner = false
  let isMinorRider = false
  let isMinorGuardian = false

  // check if an owner to sign if added as rider
  for (const paperworkDocument of paperworkDocuments) {
    for (const currRiderTeamMember of ridersTeamMembers) {
      if (currRiderTeamMember.teamMemberId === userId) {
        const isSigned = currRiderTeamMember.mailLog.find(
          (currMailLogItem) =>
            currMailLogItem.paperworkKey === paperworkDocument.key &&
            currMailLogItem.status === signedStatuses.SIGNED
        )
        let age = calculateAge(currRiderTeamMember.teamMemberDob)

        if (!isSigned?.paperworkKey && currRiderTeamMember.teamMemberDob && age > 18) {
          notSignedOwner = true
          break
        }
      }
    }
    if (notSignedOwner) break
  }

  // check if added guardian to minor rider
  for (const currRiderTeamMember of ridersTeamMembers) {
    let age = calculateAge(currRiderTeamMember.teamMemberDob)
    let isRider = currRiderTeamMember.teamMemberRole === CONST.UI.TEAM_MEMBERS_ROLES.RIDER

    let guardian = ridersTeamMembers.find(
      (teamMember) =>
        teamMember.teamMemberRole === CONST.UI.TEAM_MEMBERS_ROLES.GUARDIAN &&
        teamMember.riderId === currRiderTeamMember.riderId
    )
    let guardianAge = calculateAge(guardian?.teamMemberDob)
    if (currRiderTeamMember.teamMemberDob && age < 18 && isRider && !guardian) {
      isMinorRider = true
      break
    } else if (guardian?.teamMemberDob && guardianAge < 18) {
      isMinorGuardian = true
      break
    }
  }

  return { notSignedOwner, isMinorRider, isMinorGuardian }
}

export const generatePaperworkDocument = async ({
  paperwork,
  currRidersTeamMember,
  userData,
  eventId,
  EventDetails,
  dateOfSignature,
  isSigned,
}: {
  paperwork: IDocument
  currRidersTeamMember: IRiderTeamMemberInterface
  userData: IUserPublicInterface
  eventId: string
  EventDetails: IEventDetails
  dateOfSignature: Date
  isSigned: boolean
}) => {
  let age = calculateAge(currRidersTeamMember?.teamMemberDob)

  if (age < 18) return

  const doc = await FirestoreService.getItem(
    CONST.DATA.FIRESTORE.V01.COLLECTIONS.USERS.NAME,
    EventDetails.owner ?? ''
  )

  if (!doc) throw new Error('Error getting owner user doc')

  const organizerDetails = UserModel.fromFirestoreDoc(doc).toObject()

  const allClauses = await getAllClauses(EventDetails.owner ?? '')

  let riderAge = calculateAge(currRidersTeamMember?.riderDob)
  let isGuardian = currRidersTeamMember?.teamMemberRole === CONST.UI.TEAM_MEMBERS_ROLES.GUARDIAN

  const teamMemberRole = [currRidersTeamMember.teamMemberRole ?? '']
  const index = 0

  const payData: IGenerateDocumentPayload = {
    eventId,
    userData,
    EventDetails,
    organizerDetails,
    ridersTeamMember: currRidersTeamMember,
    paperwork,
    isMature: isGuardian ? riderAge > 18 : age > 18,
    isSigned,
    teamMemberRole,
    dateOfSignature,
    clauses: paperwork.document_type === 'general' ? allClauses : [],
    isStaff: false,
    index,
  }

  await httpService({
    url: 'generate_document',
    method: 'POST',
    data: payData,
  })
}

export const createMailLog = ({
  ridersTeamMembers,
  teamMemberId,
  paperworkDocument,
}: {
  ridersTeamMembers: IRiderTeamMemberInterface[]
  teamMemberId: string
  paperworkDocument: IDocument
}) => {
  let mailLog: IRiderTeamMemberInterface['mailLog'] = []
  let mailLogItemIndex = -1

  return ridersTeamMembers.map((current) => {
    if (current.teamMemberId === teamMemberId) {
      mailLog = cloneDeep(current.mailLog)

      mailLogItemIndex = mailLog.findIndex((curMailLog) => {
        return curMailLog.paperworkKey === paperworkDocument.key
      })

      if (mailLogItemIndex === -1) {
        mailLog.push({
          paperworkKey: paperworkDocument.key,
          status: signedStatuses.SIGNED,
        })
      } else {
        mailLog[mailLogItemIndex] = {
          ...mailLog[mailLogItemIndex],
          paperworkKey: paperworkDocument.key,
          status: signedStatuses.SIGNED,
        }
      }

      return {
        ...current,
        mailLog,
      }
    } else {
      return current
    }
  })
}
